import React, { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';

import { gql } from '@apollo/client';
import { Experiment, Variation } from '@justgiving/experiments';
import { captureException } from '@sentry/browser';

import {
  Charity,
  DonationPrompt as DonationPromptType,
  DonationPromptsFragment,
} from '../../../types/__generated__';
import { Experiments } from '../../constants/experiments';
import { UsePageQueryResult, buildQuery, usePageQuery } from '../../hooks';
import PageSection from '../PageSection/PageSection';
import DonationPrompt from './DonationPrompt';
import IconTypes from '../../constants/iconTypes';

import styles from './DonationPrompts.scss';

const FRAGMENT = buildQuery(gql`
  fragment DonationPrompts on Page {
    targetWithCurrency {
      currencyCode
    }
    reason {
      icon
    }
    relationships {
      beneficiaries(first: 1) {
        nodes {
          ... on Charity {
            __typename
            id
            displayCurrency
            donationPrompts {
              amount
              description
              type
            }
          }
        }
      }
    }
  }
`);

export const DonationPrompts: FunctionComponent = () => {
  const result = usePageQuery<DonationPromptsFragment>(FRAGMENT, {
    notifyOnNetworkStatusChange: true,
  });
  const { t } = useTranslation('common', { useSuspense: false });
  const isXLargeScreen = useMediaQuery({ minWidth: 1024 });

  if (result.loading) {
    return null;

    // B2C_041 - Donation Prompts Part 2
    // Return a loading skeleton - this is commented out as we can't
    // show the experiment until we know the user should be enrolled
    // After the experiment - if B wins, uncomment this block and
    // stop it returning null.

    // return (
    //   <DonationPromptsSkeleton
    //     onRefresh={result.refetch}
    //     animated={true}
    //     displayRefresh={false}
    //   />
    // );
  }

  if (result.error) {
    captureException(result.error);
    return null;

    // B2C_041 - Donation Prompts Part 2
    // Return a skeleton with refresh - this is commented out as we can't
    // show the experiment until we know the user should be enrolled
    // After the experiment - if B wins, uncomment this block and
    // stop it returning null.
    // return (
    //   <DonationPromptsSkeleton
    //     onRefresh={result.refetch}
    //     animated={false}
    //     displayRefresh={true}
    //   />
    // );
  }

  if (
    !UsePageQueryResult.hasData(result) ||
    !result.data?.relationships?.beneficiaries ||
    !result.data.relationships.beneficiaries.nodes[0]
  ) {
    return null;
  }

  const {
    targetWithCurrency: pageCurrency,
    reason,
    relationships: {
      beneficiaries: { nodes: beneficiaryNodes },
    },
  } = result.data;

  const {
    donationPrompts,
    displayCurrency: charityCurrencyCode,
  } = beneficiaryNodes[0] as Charity;
  const hasPageCurrencyMatchingCharityCurrency =
    (charityCurrencyCode ?? 'GBP') === pageCurrency?.currencyCode;

  const isInMemory = reason && reason.icon === IconTypes.InMemoryIcon;

  if (!hasPageCurrencyMatchingCharityCurrency) {
    return null;
  }

  const isOneOff = (prompt: DonationPromptType) => prompt.type === 'OneOff';
  const hasAValidDescription = (prompt: DonationPromptType) =>
    prompt.description && prompt.description?.trim() !== '';

  const hasAValidPositiveAmount = (prompt: DonationPromptType) =>
    typeof prompt.amount === 'number' && prompt.amount > 0;

  const isAValidDonationPrompt = (prompt: DonationPromptType) =>
    isOneOff(prompt) &&
    hasAValidDescription(prompt) &&
    hasAValidPositiveAmount(prompt);

  const sortDonationPromptsByAmountDescending = (
    { amount: amountA }: DonationPromptType,
    { amount: amountB }: DonationPromptType,
  ) => (amountB ?? 0) - (amountA ?? 0);

  const displayedDonationPrompts = (donationPrompts ?? [])
    .filter(isAValidDonationPrompt)
    .sort(sortDonationPromptsByAmountDescending)
    .map(prompt => ({
      amount: prompt.amount,
      message: prompt.description,
    }));

  const shouldEnroll = displayedDonationPrompts.length > 0 && !isInMemory;

  if (!shouldEnroll) {
    return null;
  }

  return (
    <Experiment
      slug={Experiments.b2c_041_fundraising_page_donation_prompts_2}
      skipEnrolment={!shouldEnroll}
    >
      <Variation name="B">
        <PageSection
          title={t('fundraising.donationPrompts.heading')}
          hasBorder={isXLargeScreen}
          hasBottomBorder={!isXLargeScreen}
        >
          <div className={styles.donationPrompts}>
            {displayedDonationPrompts &&
              displayedDonationPrompts.map((prompt, idx) => (
                <DonationPrompt
                  key={idx}
                  message={prompt.message}
                  amount={prompt.amount}
                  currency={charityCurrencyCode}
                />
              ))}
          </div>
        </PageSection>
      </Variation>
    </Experiment>
  );
};

export default DonationPrompts;
